import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Download,
    Loader2,
    Sparkles,
    Users,
    CreditCard,
    Activity,
    UserCheck,
    Plus,
    Minus,
    Trash2,
    Save,
    ChevronRight,
    Calendar,
    Clock,
} from "lucide-react";
import { adminGet, adminPost, getToken, logout, authHeaders } from "@/lib/adminAuth";
import { Logo } from "@/components/Logo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { key: "clients", label: "Clients", icon: UserCheck },
    { key: "dreams", label: "Dream leads", icon: Sparkles },
    { key: "leads", label: "Contact leads", icon: Users },
    { key: "payments", label: "Payments", icon: CreditCard },
    { key: "logs", label: "Access logs", icon: Activity },
];

const fmtMoney = (amountMinor, currency) => {
    if (amountMinor == null) return "—";
    const val = Number(amountMinor) / 100;
    const sym =
        currency === "usd" ? "$" : currency === "eur" ? "€" : "£";
    return `${sym}${val.toFixed(2)}`;
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const StatCard = ({ label, value, sub }) => (
    <div
        data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
        className="border border-white/10 bg-[#11141D] p-6"
    >
        <p className="overline text-white/40">{label}</p>
        <p className="font-display text-4xl md:text-5xl tracking-tighter text-[#FDFBF7] mt-2">
            {value}
        </p>
        {sub && (
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059] mt-2">
                {sub}
            </p>
        )}
    </div>
);

const downloadCSV = async (kind) => {
    try {
        const res = await fetch(`${API}/admin/export/${kind}`, {
            headers: authHeaders(),
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${kind}.csv`);
    } catch {
        toast.error("Export failed — try signing in again");
    }
};

export default function AdminDashboard() {
    const nav = useNavigate();
    const [summary, setSummary] = useState(null);
    const [upcoming, setUpcoming] = useState([]);
    const [active, setActive] = useState("clients");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);

    useEffect(() => {
        if (!getToken()) {
            nav("/admin/login", { replace: true });
            return;
        }
        (async () => {
            try {
                const s = await adminGet("/admin/summary");
                setSummary(s.data);
                const u = await adminGet("/admin/upcoming-calls");
                setUpcoming(Array.isArray(u.data) ? u.data : []);
            } catch (e) {
                if (e.response?.status === 401) {
                    toast.error("Session expired");
                    logout();
                }
            }
        })();
    }, [nav]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await adminGet(`/admin/${active}`);
                setRows(Array.isArray(r.data) ? r.data : []);
            } catch (e) {
                if (e.response?.status === 401) {
                    logout();
                    return;
                }
                toast.error("Failed to load data");
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [active]);

    const renderRow = (row, i) => {
        if (active === "clients") {
            const remaining = (row.calls_total || 0) - (row.calls_used || 0);
            const pct = row.calls_total
                ? Math.round((row.calls_used / row.calls_total) * 100)
                : 0;
            return (
                <tr
                    key={row.id}
                    data-testid={`row-client-${i}`}
                    onClick={() => setSelectedClient(row)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                >
                    <td className="p-3">{row.nome}</td>
                    <td className="p-3 font-mono text-xs text-[#C5A059]">
                        {row.email}
                    </td>
                    <td className="p-3">{row.service}</td>
                    <td className="p-3">
                        <div className="flex items-center gap-3">
                            <span className="font-mono text-sm">
                                {row.calls_used}/{row.calls_total}
                            </span>
                            <div className="w-24 h-1.5 bg-white/10">
                                <div
                                    className="h-full bg-[#C5A059] transition-all"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>
                    </td>
                    <td className="p-3 font-mono text-xs">
                        {row.next_followup
                            ? fmtDate(row.next_followup).split(",")[0]
                            : "—"}
                    </td>
                    <td className="p-3">
                        <span
                            className={`inline-block px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${
                                row.status === "completed"
                                    ? "bg-white/5 text-white/40"
                                    : remaining === 0
                                      ? "bg-[#a13d4b]/30 text-[#e58b9b]"
                                      : "bg-[#2D5A4C]/30 text-[#7fd0b3]"
                            }`}
                        >
                            {row.status}
                        </span>
                    </td>
                    <td className="p-3">
                        <ChevronRight size={14} className="text-white/30" />
                    </td>
                </tr>
            );
        }
        if (active === "dreams") {
            return (
                <tr
                    key={i}
                    data-testid={`row-dream-${i}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                    <td className="p-3">{row.nome}</td>
                    <td className="p-3 font-mono text-xs text-[#C5A059]">
                        {row.email}
                    </td>
                    <td className="p-3">{row.goal}</td>
                    <td className="p-3 font-mono">
                        {fmtMoney((row.cost || 0) * 100, row.currency)}
                    </td>
                    <td className="p-3 font-mono">
                        {fmtMoney((row.monthly || 0) * 100, row.currency)}
                    </td>
                    <td className="p-3 font-mono text-xs">
                        {row.months_estimate}mo
                    </td>
                    <td className="p-3 font-mono text-xs text-white/40">
                        {fmtDate(row.created_at)}
                    </td>
                </tr>
            );
        }
        if (active === "leads") {
            return (
                <tr
                    key={i}
                    data-testid={`row-lead-${i}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                    <td className="p-3">{row.nome}</td>
                    <td className="p-3 font-mono text-xs text-[#C5A059]">
                        {row.email}
                    </td>
                    <td className="p-3">{row.servizio}</td>
                    <td className="p-3 text-sm text-white/70 max-w-xs truncate">
                        {row.messaggio}
                    </td>
                    <td className="p-3 font-mono text-xs text-white/40">
                        {fmtDate(row.created_at)}
                    </td>
                </tr>
            );
        }
        if (active === "payments") {
            const paid = row.payment_status === "paid";
            return (
                <tr
                    key={i}
                    data-testid={`row-payment-${i}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                    <td className="p-3 font-mono text-xs">
                        {row.email || "—"}
                    </td>
                    <td className="p-3">{row.service}</td>
                    <td className="p-3 font-mono">
                        {fmtMoney(row.amount, row.currency)}
                    </td>
                    <td className="p-3">
                        <span
                            className={`inline-block px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${paid ? "bg-[#2D5A4C]/30 text-[#7fd0b3]" : "bg-white/5 text-white/50"}`}
                        >
                            {row.payment_status}
                        </span>
                    </td>
                    <td className="p-3 font-mono text-xs text-white/40">
                        {fmtDate(row.created_at)}
                    </td>
                </tr>
            );
        }
        // logs
        return (
            <tr
                key={i}
                data-testid={`row-log-${i}`}
                className="border-b border-white/5 hover:bg-white/[0.02]"
            >
                <td className="p-3 font-mono text-xs">{row.ip}</td>
                <td className="p-3 font-mono text-xs text-[#C5A059]">
                    {row.email}
                </td>
                <td className="p-3">{row.event}</td>
                <td className="p-3">
                    <span
                        className={`font-mono text-xs ${row.success ? "text-[#7fd0b3]" : "text-[#e58b9b]"}`}
                    >
                        {row.success ? "✓ ok" : "✗ fail"}
                    </span>
                </td>
                <td className="p-3 font-mono text-xs text-white/40">
                    {fmtDate(row.at)}
                </td>
            </tr>
        );
    };

    const headers = {
        clients: [
            "Name",
            "Email",
            "Service",
            "Calls",
            "Next follow-up",
            "Status",
            "",
        ],
        dreams: ["Name", "Email", "Goal", "Cost", "Monthly", "Est.", "Date"],
        leads: ["Name", "Email", "Service", "Message", "Date"],
        payments: ["Email", "Service", "Amount", "Status", "Date"],
        logs: ["IP", "Email", "Event", "Result", "When"],
    };

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#070A13] text-[#FDFBF7] grain"
        >
            <header className="border-b border-white/10 sticky top-0 z-20 bg-[#070A13]/95 backdrop-blur-md">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo className="w-9 h-9" dark />
                        <span className="font-display text-xl">The Raw Math</span>
                        <span className="overline text-[#C5A059]">/ Admin</span>
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={logout}
                        className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
                <div className="mb-10">
                    <p className="overline text-[#C5A059] mb-3">
                        — Command centre
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tighter">
                        Every lead. Every pound. In one place.
                    </h1>
                </div>

                {/* Upcoming calls widget */}
                <UpcomingCalls
                    calls={upcoming}
                    onOpen={(clientId) => {
                        const c = rows.find((r) => r.id === clientId);
                        if (c) setSelectedClient(c);
                        else {
                            adminGet(`/admin/clients/${clientId}`)
                                .then((r) => setSelectedClient(r.data))
                                .catch(() => {});
                        }
                    }}
                />

                {/* Summary */}
                <div
                    data-testid="admin-summary"
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
                >
                    {summary ? (
                        <>
                            <StatCard
                                label="Dream leads"
                                value={summary.dream_leads}
                                sub="From the calculator"
                            />
                            <StatCard
                                label="Contact leads"
                                value={summary.contact_leads}
                                sub="From the form"
                            />
                            <StatCard
                                label="Paid"
                                value={summary.payments_paid}
                                sub={`${summary.payments_pending} pending`}
                            />
                            <StatCard
                                label="Revenue"
                                value={`£${(summary.revenue_minor_units / 100).toFixed(0)}`}
                                sub="Mixed currencies · raw"
                            />
                        </>
                    ) : (
                        <div className="col-span-4 py-10 text-center text-white/40">
                            <Loader2
                                size={20}
                                className="animate-spin inline-block"
                            />
                        </div>
                    )}
                </div>

                {/* Tabs + export */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div
                        data-testid="admin-tabs"
                        className="flex flex-wrap gap-1 border border-white/10"
                    >
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const isActive = active === t.key;
                            return (
                                <button
                                    key={t.key}
                                    data-testid={`tab-${t.key}`}
                                    onClick={() => setActive(t.key)}
                                    className={`inline-flex items-center gap-2 px-5 py-3 font-body text-sm transition-colors ${
                                        isActive
                                            ? "bg-[#C5A059] text-[#070A13]"
                                            : "text-white/60 hover:text-white hover:bg-white/5"
                                    }`}
                                >
                                    <Icon size={14} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {active === "clients" ? (
                        <button
                            data-testid="clients-backfill"
                            onClick={async () => {
                                try {
                                    await adminPost("/admin/backfill-clients", {});
                                    toast.success("Backfilled from past payments");
                                    // refresh
                                    const r = await adminGet(`/admin/clients`);
                                    setRows(Array.isArray(r.data) ? r.data : []);
                                } catch {
                                    toast.error("Backfill failed");
                                }
                            }}
                            className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#070A13] px-5 py-3 font-body text-sm transition-colors self-start"
                        >
                            <Save size={14} />
                            Backfill from past payments
                        </button>
                    ) : active !== "logs" ? (
                        <button
                            data-testid={`export-${active}`}
                            onClick={() => downloadCSV(active)}
                            className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#070A13] px-5 py-3 font-body text-sm transition-colors self-start"
                        >
                            <Download size={14} />
                            Export CSV
                        </button>
                    ) : null}
                </div>

                {/* Table */}
                <div className="border border-white/10 bg-[#11141D] overflow-x-auto">
                    {loading ? (
                        <div className="py-20 text-center text-white/40">
                            <Loader2
                                size={20}
                                className="animate-spin inline-block"
                            />
                        </div>
                    ) : rows.length === 0 ? (
                        <div
                            data-testid={`empty-${active}`}
                            className="py-20 text-center text-white/40 font-body"
                        >
                            Nothing here yet.
                        </div>
                    ) : (
                        <table className="w-full text-left font-body text-sm">
                            <thead>
                                <tr className="border-b border-white/10 bg-white/[0.02]">
                                    {headers[active].map((h) => (
                                        <th
                                            key={h}
                                            className="p-3 overline text-white/50"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody data-testid={`table-${active}`}>
                                {rows.map(renderRow)}
                            </tbody>
                        </table>
                    )}
                </div>

                <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30 text-center">
                    Signed in as admin · session expires in 8h · all reads are
                    logged
                </p>
            </main>

            {selectedClient && (
                <ClientDetail
                    client={selectedClient}
                    onClose={() => setSelectedClient(null)}
                    onUpdate={async () => {
                        // refresh table + selected client details
                        try {
                            const r = await adminGet("/admin/clients");
                            const list = Array.isArray(r.data) ? r.data : [];
                            setRows(list);
                            const fresh = list.find(
                                (c) => c.id === selectedClient.id
                            );
                            if (fresh) setSelectedClient(fresh);
                        } catch {
                            // ignore refresh errors
                        }
                    }}
                />
            )}
        </div>
    );
}

function UpcomingCalls({ calls, onOpen }) {
    if (!calls || calls.length === 0) {
        return (
            <div
                data-testid="upcoming-calls-empty"
                className="mb-10 border border-dashed border-white/10 px-6 py-5 flex items-center gap-3"
            >
                <Calendar size={16} className="text-white/30" />
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-white/40">
                    No calls booked yet — once clients pay and book, they show
                    up here.
                </p>
            </div>
        );
    }

    const fmtWhen = (iso) => {
        try {
            const d = new Date(iso);
            const now = new Date();
            const diffH = (d - now) / (1000 * 60 * 60);
            const relative =
                diffH < 24
                    ? `in ${Math.round(diffH)}h`
                    : diffH < 168
                      ? `in ${Math.round(diffH / 24)}d`
                      : "";
            const day = d.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "2-digit",
                month: "short",
            });
            const time = d.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
            });
            return { day, time, relative };
        } catch {
            return { day: iso, time: "", relative: "" };
        }
    };

    return (
        <div
            data-testid="upcoming-calls"
            className="mb-10 border border-[#C5A059]/25 bg-gradient-to-br from-[#0f1220] to-[#11141D] p-6 md:p-8"
        >
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30">
                        <Calendar size={16} className="text-[#C5A059]" />
                    </span>
                    <div>
                        <p className="overline text-[#C5A059]">
                            — Upcoming calls
                        </p>
                        <p className="font-display text-2xl tracking-tighter mt-1">
                            Your next {calls.length}{" "}
                            {calls.length === 1 ? "call" : "calls"}
                        </p>
                    </div>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 hidden md:block">
                    Auto-updates when clients book on Calendly
                </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {calls.slice(0, 6).map((c, i) => {
                    const w = fmtWhen(c.event_start);
                    const isNext = i === 0;
                    return (
                        <button
                            key={`${c.client_id}-${c.event_start}-${i}`}
                            data-testid={`upcoming-call-${i}`}
                            onClick={() => onOpen(c.client_id)}
                            className={`text-left p-4 border transition-colors flex items-start gap-4 group ${
                                isNext
                                    ? "border-[#C5A059]/50 bg-[#C5A059]/[0.04]"
                                    : "border-white/10 hover:border-white/25 bg-[#070A13]/40"
                            }`}
                        >
                            <div className="shrink-0 text-center">
                                <p
                                    className={`font-mono text-[10px] uppercase tracking-[0.15em] ${isNext ? "text-[#C5A059]" : "text-white/40"}`}
                                >
                                    {w.day}
                                </p>
                                <p className="font-display text-2xl tracking-tighter text-[#FDFBF7] mt-1">
                                    {w.time}
                                </p>
                                {w.relative && (
                                    <p className="font-mono text-[9px] uppercase tracking-wider text-white/40 mt-1">
                                        {w.relative}
                                    </p>
                                )}
                            </div>
                            <div className="flex-1 min-w-0 border-l border-white/10 pl-4">
                                <p className="font-body text-sm text-[#FDFBF7] truncate">
                                    {c.client_name}
                                </p>
                                <p className="font-mono text-[10px] text-[#C5A059] truncate mt-0.5">
                                    {c.client_email}
                                </p>
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                                        {c.service}
                                    </span>
                                    <span className="font-mono text-[10px] text-white/30">
                                        ·
                                    </span>
                                    <span className="font-mono text-[10px] text-white/50">
                                        {c.calls_used}/{c.calls_total}
                                    </span>
                                </div>
                            </div>
                            <ChevronRight
                                size={14}
                                className="text-white/20 group-hover:text-[#C5A059] transition-colors shrink-0 mt-1"
                            />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ClientDetail({ client, onClose, onUpdate }) {
    const [note, setNote] = useState("");
    const [followup, setFollowup] = useState(
        client.next_followup ? client.next_followup.slice(0, 10) : ""
    );
    const [saving, setSaving] = useState(false);
    const [showLogForm, setShowLogForm] = useState(false);
    const [logDate, setLogDate] = useState(() => new Date().toISOString().slice(0, 16));
    const [logNote, setLogNote] = useState("");

    const remaining = (client.calls_total || 0) - (client.calls_used || 0);

    const logCall = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminPost(`/admin/clients/${client.id}/log-call`, {
                event_start: new Date(logDate).toISOString(),
                note: logNote.trim() || null,
            });
            toast.success("Call logged — counter updated");
            setShowLogForm(false);
            setLogNote("");
            setLogDate(new Date().toISOString().slice(0, 16));
            await onUpdate();
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to log call";
            toast.error(typeof detail === "string" ? detail : "Failed to log call");
        } finally {
            setSaving(false);
        }
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setSaving(true);
        try {
            await adminPost(`/admin/clients/${client.id}/notes`, {
                text: note.trim(),
            });
            setNote("");
            toast.success("Note saved");
            await onUpdate();
        } catch {
            toast.error("Failed to save note");
        } finally {
            setSaving(false);
        }
    };

    const deleteNote = async (noteId) => {
        try {
            await axios.delete(`${API}/admin/clients/${client.id}/notes/${noteId}`, {
                headers: authHeaders(),
            });
            toast.success("Note removed");
            await onUpdate();
        } catch {
            toast.error("Failed to delete note");
        }
    };

    const saveFollowup = async () => {
        setSaving(true);
        try {
            await axios.patch(
                `${API}/admin/clients/${client.id}/followup`,
                { next_followup: followup ? new Date(followup).toISOString() : null },
                { headers: authHeaders() }
            );
            toast.success("Follow-up date saved");
            await onUpdate();
        } catch {
            toast.error("Failed to save date");
        } finally {
            setSaving(false);
        }
    };

    const adjustCalls = async (delta) => {
        try {
            await adminPost(`/admin/clients/${client.id}/adjust`, {
                delta,
                reason: delta > 0 ? "manual mark used" : "manual restore",
            });
            toast.success(delta > 0 ? "Call marked used" : "Call restored");
            await onUpdate();
        } catch {
            toast.error("Failed to adjust");
        }
    };

    return (
        <div
            data-testid="client-detail-modal"
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-[#0d1019] border border-[#C5A059]/30 max-w-2xl w-full my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-8 border-b border-white/10 flex items-start justify-between gap-4">
                    <div>
                        <p className="overline text-[#C5A059] mb-2">
                            {client.service}
                        </p>
                        <h2 className="font-display text-3xl tracking-tighter">
                            {client.nome}
                        </h2>
                        <p className="font-mono text-xs text-white/50 mt-2">
                            {client.email}
                        </p>
                    </div>
                    <button
                        data-testid="client-detail-close"
                        onClick={onClose}
                        className="text-white/40 hover:text-white text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Call counter */}
                <div className="p-8 border-b border-white/10">
                    <div className="flex items-center justify-between mb-4">
                        <p className="overline text-white/40">Calls used</p>
                        <div className="flex gap-1.5">
                            <button
                                data-testid="client-adjust-down"
                                onClick={() => adjustCalls(-1)}
                                disabled={client.calls_used === 0}
                                title="Undo last call (courtesy restore)"
                                className="w-8 h-8 border border-white/10 text-white/40 hover:border-[#C5A059]/40 hover:text-[#C5A059] disabled:opacity-30 flex items-center justify-center transition-colors"
                            >
                                <Minus size={12} />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-end gap-4 mb-6">
                        <span className="font-display text-6xl tracking-tighter text-[#FDFBF7] leading-none">
                            {client.calls_used}
                        </span>
                        <span className="font-display text-3xl text-white/30 pb-2">
                            /
                        </span>
                        <span className="font-display text-6xl tracking-tighter text-[#C5A059] leading-none">
                            {client.calls_total}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 ml-auto pb-2">
                            {remaining} remaining
                        </span>
                    </div>

                    {/* Log a call — primary CTA */}
                    {remaining > 0 &&
                        (showLogForm ? (
                            <form
                                onSubmit={logCall}
                                data-testid="client-log-form"
                                className="border border-[#C5A059]/40 bg-[#C5A059]/[0.04] p-5 space-y-4"
                            >
                                <div>
                                    <label className="overline text-[#C5A059] block mb-2">
                                        When did the call happen?
                                    </label>
                                    <input
                                        data-testid="client-log-date"
                                        type="datetime-local"
                                        required
                                        value={logDate}
                                        onChange={(e) => setLogDate(e.target.value)}
                                        className="w-full bg-[#11141D] border border-white/10 px-4 py-3 font-mono text-sm text-[#FDFBF7] focus:outline-none focus:border-[#C5A059]"
                                    />
                                </div>
                                <div>
                                    <label className="overline text-[#C5A059] block mb-2">
                                        Quick note (optional)
                                    </label>
                                    <textarea
                                        data-testid="client-log-note"
                                        value={logNote}
                                        onChange={(e) => setLogNote(e.target.value)}
                                        rows={2}
                                        placeholder="e.g. Diagnosis — red zone, £412/mo of leaks"
                                        className="w-full bg-[#11141D] border border-white/10 px-4 py-3 font-body text-sm text-[#FDFBF7] focus:outline-none focus:border-[#C5A059] resize-none"
                                    />
                                </div>
                                <div className="flex gap-3 pt-1">
                                    <button
                                        data-testid="client-log-submit"
                                        type="submit"
                                        disabled={saving}
                                        className="flex-1 bg-[#C5A059] text-[#070A13] px-6 py-3 font-body text-sm font-semibold hover:bg-[#d9b675] transition-colors disabled:opacity-60"
                                    >
                                        {saving
                                            ? "Logging…"
                                            : `Log call ${client.calls_used + 1}/${client.calls_total}`}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowLogForm(false)}
                                        className="px-6 py-3 font-body text-sm text-white/50 hover:text-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <button
                                data-testid="client-log-open"
                                onClick={() => setShowLogForm(true)}
                                className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#070A13] px-6 py-4 font-body font-semibold hover:bg-[#d9b675] transition-colors"
                            >
                                <Plus size={16} />
                                Log a call — counter auto-updates
                            </button>
                        ))}
                    {remaining === 0 && (
                        <div className="border border-white/10 bg-white/5 px-4 py-4 text-center">
                            <p className="font-mono text-xs uppercase tracking-[0.15em] text-white/50">
                                All {client.calls_total} calls used ·{" "}
                                {client.service === "The Signal"
                                    ? "consider upselling to Dream Plan"
                                    : "cycle complete"}
                            </p>
                        </div>
                    )}

                    {client.bookings?.length > 0 && (
                        <div className="mt-6 space-y-2">
                            <p className="overline text-white/40">
                                Booking history
                            </p>
                            {client.bookings.slice(-5).reverse().map((b, i) => (
                                <div
                                    key={i}
                                    className="flex items-center justify-between font-mono text-xs text-white/60 border-l-2 border-[#C5A059]/40 pl-3 py-1"
                                >
                                    <span>
                                        {b.type === "adjustment"
                                            ? `Manual ${b.delta > 0 ? "+" : ""}${b.delta}${b.reason ? ` · ${b.reason}` : ""}`
                                            : b.status === "logged_manual"
                                              ? `Logged · ${b.event_start ? new Date(b.event_start).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}`
                                              : `Booked · ${b.event_start ? new Date(b.event_start).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "TBD"}`}
                                    </span>
                                    <span className="text-white/30">
                                        {b.status || ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Next followup */}
                <div className="p-8 border-b border-white/10">
                    <p className="overline text-white/40 mb-3">
                        Next follow-up
                    </p>
                    <div className="flex gap-3">
                        <input
                            data-testid="client-followup-input"
                            type="date"
                            value={followup}
                            onChange={(e) => setFollowup(e.target.value)}
                            className="flex-1 bg-[#11141D] border border-white/10 px-4 py-3 font-mono text-sm text-[#FDFBF7] focus:outline-none focus:border-[#C5A059]"
                        />
                        <button
                            data-testid="client-followup-save"
                            onClick={saveFollowup}
                            disabled={saving}
                            className="bg-[#C5A059] text-[#070A13] px-5 py-3 font-body text-sm font-semibold hover:bg-[#d9b675] transition-colors disabled:opacity-60"
                        >
                            Save
                        </button>
                    </div>
                </div>

                {/* Notes */}
                <div className="p-8">
                    <p className="overline text-white/40 mb-3">Session notes</p>
                    <form onSubmit={addNote} className="flex gap-3 mb-6">
                        <textarea
                            data-testid="client-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. 22 Jul · diagnosis call — red zone, £412/mo of leaks, recommended cancelling 3 subs"
                            rows={2}
                            className="flex-1 bg-[#11141D] border border-white/10 px-4 py-3 font-body text-sm text-[#FDFBF7] focus:outline-none focus:border-[#C5A059] resize-none"
                        />
                        <button
                            data-testid="client-note-add"
                            type="submit"
                            disabled={saving || !note.trim()}
                            className="bg-[#C5A059] text-[#070A13] px-5 py-3 font-body text-sm font-semibold hover:bg-[#d9b675] transition-colors disabled:opacity-60 self-start"
                        >
                            Add
                        </button>
                    </form>
                    <div className="space-y-3">
                        {(client.notes || []).length === 0 ? (
                            <p className="font-body text-sm text-white/30 text-center py-6">
                                No notes yet.
                            </p>
                        ) : (
                            client.notes
                                .slice()
                                .reverse()
                                .map((n) => (
                                    <div
                                        key={n.id}
                                        data-testid={`note-${n.id}`}
                                        className="border border-white/10 p-4 flex items-start gap-3"
                                    >
                                        <div className="flex-1">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059] mb-2">
                                                {fmtDate(n.at)}
                                            </p>
                                            <p className="font-body text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                                                {n.text}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => deleteNote(n.id)}
                                            className="text-white/30 hover:text-[#a13d4b] transition-colors shrink-0"
                                            title="Delete note"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
